/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useState } from 'react'

import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import type { AdminAssetGroup } from '../types'

const log = logger({ category: 'useAdminAssetGroups' })

export interface AdminAssetGroupsState {
  groups: AdminAssetGroup[]
  isLoading: boolean
  error: string
}

/**
 * Every current customer's asset groups, with their counts and profile lists — for the
 * user list's asset group column and filter, and the asset group admin list.
 */
export const useAdminAssetGroups = (): AdminAssetGroupsState => {
  const { api } = useApi()
  const [groups, setGroups] = useState<AdminAssetGroup[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const response = await api.get<AdminAssetGroup[]>({
          path: '/app/admin/asset-groups',
          origin: 'useAdminAssetGroups',
        })
        if (cancelled) return
        if (response.error) {
          setError(response.message || 'Could not load asset groups')
        } else {
          setError('')
          setGroups(response.data ?? [])
        }
      } catch (ex) {
        log.error(ex)
        if (!cancelled) setError('Could not load asset groups')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [api])

  return { groups, isLoading, error }
}
